import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthCard } from '@sentinel/ui';
import { useLogout } from '../features/logout/use-logout';

const search = z.object({
  id_token_hint: z.string().optional(),
  post_logout_redirect_uri: z.string().optional(),
  state: z.string().optional(),
});

type LogoutSearch = z.infer<typeof search>;

function LogoutPage() {
  const params = useSearch({ from: '/logout' });
  const isOidcRpInitiated = Boolean(params.id_token_hint || params.post_logout_redirect_uri);

  if (isOidcRpInitiated) {
    return <RpInitiatedLogout params={params} />;
  }
  return <SpaLogout />;
}

function RpInitiatedLogout({ params }: { params: LogoutSearch }) {
  const { t } = useTranslation('logout');
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    formRef.current?.submit();
  }, []);

  return (
    <AuthCard title={t('title')} subtitle={t('rp_initiated_continue')}>
      <form ref={formRef} method="post" action="/auth/end-session">
        {params.id_token_hint ? (
          <input type="hidden" name="id_token_hint" value={params.id_token_hint} />
        ) : null}
        {params.post_logout_redirect_uri ? (
          <input
            type="hidden"
            name="post_logout_redirect_uri"
            value={params.post_logout_redirect_uri}
          />
        ) : null}
        {params.state ? <input type="hidden" name="state" value={params.state} /> : null}
      </form>
    </AuthCard>
  );
}

function SpaLogout() {
  const { t } = useTranslation('logout');
  const navigate = useNavigate();
  const logout = useLogout();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void (async () => {
      await logout.mutateAsync();
      const id = window.setTimeout(() => navigate({ to: '/login' }), 1500);
      return () => window.clearTimeout(id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthCard title={t('title')} subtitle={t('message')}>
      <p>{t('redirecting')}</p>
      <p>
        <Link to="/login">{t('sign_in_again')}</Link>
      </p>
    </AuthCard>
  );
}

export const Route = createFileRoute('/logout')({
  validateSearch: search,
  component: LogoutPage,
});
